// docs/47 — spells drawn on the board. Every spell is gathered in the caster's hands (a casting
// circle at its feet, motes drawn in, a growing glow), then flies, falls, spreads or settles by
// its form, and lands at the clip's impact with its element's flash, particles and shake; some
// leave something behind for a moment (embers, frost, a puddle, falling leaves). Drawn in the
// board's effects layer, over the light map, so a fireball lights the night. Pixel particles,
// soft glows; the effect-intensity setting thins the particles, reduce motion stills the shake
// and the flight, "lightning flashes" off keeps the board from flashing.
import Phaser from 'phaser';
import type { Tile } from '../../core/sim/types';
import { PALETTES, WINDUP_MS, castPalette, type Element, type Palette, type SpellCast } from '../../core/playback/spells';
import type { UnitSprite } from '../board/UnitSprite';
import { faceOf } from '../hugeArt';
import { NOTES, TEX, ensureSpellTextures } from './textures';

export interface SpellHost {
  scene: Phaser.Scene;
  /** The board's effects layer (board camera, board pixels). */
  add(o: Phaser.GameObjects.GameObject): void;
  /** On the ground, under the figures (a casting circle, a scorch, frost, a puddle); else `add`. */
  addGround?(o: Phaser.GameObjects.GameObject): void;
  cell: number;
  tileCentre(t: Tile): { x: number; y: number };
  unit(id: number): UnitSprite | undefined;
  /** The board camera, for the shake. */
  camera?: Phaser.Cameras.Scene2D.Camera;
  /** The board's size in board pixels, for a flash over the whole of it. */
  boardSize?(): { w: number; h: number } | undefined;
}

export interface SpellQuality {
  /** 0–1: how many particles (the effect-intensity setting). */
  density: number;
  /** Reduce motion: no shake, no flight wobble, short trails. */
  still: boolean;
  /** The board may flash white (off with the lightning-flash setting). */
  flashes: boolean;
}

type P = { x: number; y: number };
/** The longest any piece of a spell stays on the board (lingering embers and scorches included). */
const MAX_LIFE_MS = 4500;
type EmitCfg = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;
const ADD = Phaser.BlendModes.ADD;

/** What each element throws as particles: a texture, a colour ramp over each grain's life, its fall. */
interface Grain { tex: string; tint: number[]; ramp: number[]; gravity: number; rotate?: boolean; blend: Phaser.BlendModes; life: number }
function grain(el: Element, p: Palette): Grain {
  const ramp = [p.core, p.glow, p.edge, p.dark];
  const N = Phaser.BlendModes.NORMAL;
  // bodies of the element are drawn NORMAL so their colour holds on bright grass and snow; only
  // sparks and light are added
  switch (el) {
    case 'fire': return { tex: TEX.flame, tint: [p.core, p.glow, p.edge], ramp: [0xffe45a, 0xffa22a, 0xf05a14, 0xb8280c, 0x4a2014], gravity: -70, blend: N, life: 620 };
    case 'radiant': return { tex: TEX.flame, tint: [p.core, p.glow, p.edge], ramp: [0xfffbe0, 0xffe070, 0xffb02a, 0xe8701a, 0x6a3a10], gravity: -70, blend: N, life: 620 };
    case 'frost': return { tex: TEX.flake, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, 0xc8f0ff, 0x6cc6ff, 0x2f78c8], gravity: 30, rotate: true, blend: N, life: 780 };
    case 'arcane': case 'wild': return { tex: TEX.star, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, p.glow, p.edge, p.dark], gravity: 0, blend: N, life: 560 };
    case 'force': return { tex: TEX.dot, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, p.glow, p.edge, p.dark], gravity: 0, blend: N, life: 460 };
    case 'lightning': case 'thunder': case 'steel': return { tex: TEX.px, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, p.glow, p.edge], gravity: 0, blend: ADD, life: 420 };
    case 'acid': case 'poison': return { tex: TEX.drop, tint: [p.core, p.glow, p.edge], ramp: [p.core, p.glow, p.edge, p.dark], gravity: 170, blend: N, life: 640 };
    case 'holy': return { tex: TEX.star, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, 0xfff0a0, 0xffc23a, 0xc8801a], gravity: -25, blend: N, life: 760 };
    case 'shadow': return { tex: TEX.dot, tint: [p.glow, p.edge, p.dark], ramp: [0xd6b2ff, p.glow, p.edge, p.dark], gravity: -15, blend: N, life: 700 };
    case 'nature': return { tex: TEX.leaf, tint: [p.glow, p.edge, p.dark], ramp: [p.core, p.glow, p.edge, p.dark], gravity: 70, rotate: true, blend: N, life: 860 };
    case 'life': return { tex: TEX.plus, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, p.glow, p.edge], gravity: -35, blend: N, life: 860 };
    case 'smoke': return { tex: TEX.smoke, tint: [p.glow, p.edge], ramp, gravity: -10, blend: N, life: 1000 };
    // docs/104: thrown earth, blown leaves, notes
    case 'earth': return { tex: TEX.pebble, tint: [p.glow, p.edge, p.dark], ramp: [p.glow, p.edge, p.dark], gravity: 190, rotate: true, blend: N, life: 640 };
    case 'wind': return { tex: TEX.leaf, tint: [0x9be06a, 0xc9a24a, p.edge], ramp: [0xd8f0a0, 0x9be06a, 0x5a8a3a], gravity: 0, rotate: true, blend: N, life: 640 };
    case 'music': return { tex: TEX.note1, tint: [p.core, p.glow, p.edge], ramp: [0xffffff, p.glow, p.edge], gravity: -40, blend: N, life: 900 };
  }
}

/** The grain's colours as an emitter config: a ramp over the life when it is lively, else a tint. */
const hue = (g: Grain, ramp = true): Partial<EmitCfg> => (ramp && g.ramp.length > 1 ? { color: g.ramp, colorEase: 'Quad.easeOut' } : { tint: g.tint });

export class SpellFx {
  private live = new Set<Phaser.GameObjects.GameObject>();
  private timers: Phaser.Time.TimerEvent[] = [];

  constructor(private readonly host: SpellHost, private quality: SpellQuality) {
    ensureSpellTextures(host.scene);
  }

  setQuality(q: SpellQuality): void { this.quality = q; }

  /** Everything still flying or burning goes (a skipped playback, the scene leaving). */
  clear(): void {
    for (const t of this.timers) t.remove(false);
    this.timers = [];
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  /**
   * Plays `c` over `ms` (already at the playback's `speed`) with its landing at `impactMs`;
   * `onImpact` runs at the landing (the caller's numbers and flashes), the promise settles at `ms`.
   */
  play(c: SpellCast, ms: number, impactMs: number, speed = 1, onImpact?: () => void): Promise<void> {
    const pal = castPalette(c);
    const hand = this.hand(c);
    const aim = this.aimPoint(c);
    // the windup is the director's too (its travel sound starts there): never squeezed but by the speed
    const windup = Math.min(impactMs, WINDUP_MS / Math.max(1, speed));
    const travel = Math.max(1, impactMs - windup);
    this.gather(c, hand, pal, windup);
    this.after(windup, () => this.fly(c, hand, aim, pal, travel));
    this.after(impactMs, () => { this.land(c, aim, pal, Math.max(120, ms - impactMs)); onImpact?.(); });
    return new Promise((resolve) => this.after(ms, resolve));
  }

  // ---- geometry -------------------------------------------------------------------------------

  private centreOfUnit(id: number): P | undefined {
    const u = this.host.unit(id);
    return u ? { x: u.x, y: u.y - this.host.cell * 0.5 } : undefined;
  }

  /** Where the spell leaves the caster: a little in front of its chest. */
  private hand(c: SpellCast): P {
    const u = this.host.unit(c.caster);
    const cell = this.host.cell;
    if (u) {
      const facing = faceOf(u.sprite);
      return { x: u.x + facing * cell * 0.22, y: u.y - cell * 0.62 };
    }
    const p = this.host.tileCentre(c.from);
    return { x: p.x, y: p.y - cell * 0.1 };
  }

  private tileP(t: Tile): P { const p = this.host.tileCentre(t); return { x: p.x, y: p.y }; }

  /** The spell's destination: its first target's body, else its aim square. */
  private aimPoint(c: SpellCast): P {
    const f = c.look.form;
    const unitFirst = f === 'bolt' || f === 'darts' || f === 'touch' || f === 'curse' || f === 'aura' || f === 'smite' || f === 'jeer' || f === 'song';
    if (unitFirst && c.targets.length) { const p = this.centreOfUnit(c.targets[0]!); if (p) return p; }
    if (c.aim) return this.tileP(c.aim);
    if (c.targetTiles[0]) return this.tileP(c.targetTiles[0]);
    return this.hand(c);
  }

  private areaPoints(c: SpellCast): P[] {
    if (c.tiles.length) return c.tiles.map((t) => this.tileP(t));
    if (c.targetTiles.length) return c.targetTiles.map((t) => this.tileP(t));
    return [this.aimPoint(c)];
  }

  private bodies(c: SpellCast): P[] {
    const out = c.targets.map((u) => this.centreOfUnit(u)).filter((p): p is P => !!p);
    return out.length ? out : [this.aimPoint(c)];
  }

  // ---- the three beats --------------------------------------------------------------------------

  /** The windup: a casting circle under the caster, motes drawn into the hands, a growing glow. */
  private gather(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const u = this.host.unit(c.caster);
    const cell = this.host.cell;
    const feet = u ? { x: u.x, y: u.y - 2 } : hand;
    const musical = c.look.form === 'song' || c.look.form === 'jeer' || c.look.form === 'finale';
    const touchy = c.look.form === 'aura' || c.look.form === 'cloud' || musical;
    // the circle on the ground, squashed to lie flat, and its light
    const pool = this.ground(feet.x, feet.y, TEX.glow, pal.edge, ADD).setScale((cell * 1.2) / 64, (cell * 0.5) / 64).setAlpha(0);
    this.tween({ targets: pool, alpha: 0.9, duration: ms * 0.5 });
    this.after(ms, () => this.fade(pool, 260));
    // docs/104 BD: a song needs no casting circle — the bard simply plays (its notes are below)
    if (musical) { this.gatherSong(c, hand, pal, ms); return; }
    const rune = this.ground(feet.x, feet.y, TEX.rune, pal.glow, ADD).setScale(0.05, 0.02).setAlpha(0);
    this.tween({ targets: rune, scaleX: (cell * 1.7) / 64, scaleY: (cell * 0.7) / 64, alpha: 1, angle: 120, duration: ms * 0.7, ease: 'Back.easeOut' });
    this.tween({ targets: rune, alpha: 0, angle: 260, duration: ms * 0.6, delay: ms * 0.8, ease: 'Quad.easeIn', onComplete: () => this.kill(rune) });
    const inner = this.ground(feet.x, feet.y, TEX.rune, pal.core, ADD).setScale(0.05, 0.02).setAlpha(0);
    this.tween({ targets: inner, scaleX: (cell * 0.9) / 64, scaleY: (cell * 0.36) / 64, alpha: 0.8, angle: -160, duration: ms * 0.8, ease: 'Sine.easeOut' });
    this.tween({ targets: inner, alpha: 0, duration: ms * 0.5, delay: ms * 0.85, onComplete: () => this.kill(inner) });
    // sparks lifting off the circle
    const lift = this.emitter(feet.x, feet.y, TEX.px, { lifespan: ms, speedY: { min: -50, max: -20 }, speedX: { min: -4, max: 4 }, scale: { start: 1, end: 0 }, color: [0xffffff, pal.glow, pal.edge], blendMode: ADD, frequency: this.quality.density > 0.5 ? 22 : 60,
      emitZone: { type: 'edge', source: new Phaser.Geom.Ellipse(0, 0, cell * 1.5, cell * 0.6) as unknown as Phaser.Types.GameObjects.Particles.EdgeZoneSource, quantity: 24 } });
    this.after(ms * 0.9, () => { lift.stop(); this.reap(lift, ms); });
    // motes spiralling in to the hands
    const n = this.n(touchy ? 12 : 22);
    const e = this.emitter(hand.x, hand.y, TEX.star, {
      emitting: false, lifespan: ms * 0.85, scale: { start: 1.2, end: 0.4 }, alpha: { start: 0.3, end: 1 },
      color: [pal.edge, pal.glow, 0xffffff], blendMode: ADD, moveToX: hand.x, moveToY: hand.y,
      emitZone: { type: 'random', source: new Phaser.Geom.Circle(0, 0, cell * 1.1) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
    });
    this.burst(e, n, hand.x, hand.y);
    this.reap(e, ms);
    // the gathered light, pulsing as it grows
    const halo = this.image(hand.x, hand.y, TEX.glow, pal.edge, ADD).setScale(0.02).setAlpha(0.3);
    const orb = this.image(hand.x, hand.y, TEX.glow, pal.glow, ADD).setScale(0.02).setAlpha(0.4);
    const core = this.image(hand.x, hand.y, TEX.star, 0xffffff, ADD).setScale(0.3);
    this.tween({ targets: halo, scale: (cell * 1.6) / 64, alpha: 0.7, duration: ms, ease: 'Sine.easeIn' });
    this.tween({ targets: orb, scale: (cell * 0.8) / 64, alpha: 1, duration: ms, ease: 'Sine.easeIn' });
    this.tween({ targets: core, scale: 1.6, angle: 180, duration: ms, ease: 'Sine.easeIn' });
    this.after(ms + 20, () => { this.fade(halo, 160); this.fade(orb, 120); this.fade(core, 80); });
    // the caster rises onto its toes
    if (u && !this.quality.still) this.tween({ targets: u.sprite, scaleY: 1.08, duration: ms * 0.5, yoyo: true, ease: 'Sine.easeInOut' });
  }

  /** docs/104 BD: a bard strikes up — a warm glow at the instrument, the bard bobs to the beat, and the first notes lift off it. */
  private gatherSong(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const u = this.host.unit(c.caster);
    const glow = this.image(hand.x, hand.y + cell * 0.1, TEX.glow, pal.edge, ADD).setScale(0.05).setAlpha(0.2);
    this.tween({ targets: glow, scale: (cell * 1.1) / 64, alpha: 0.75, duration: ms, ease: 'Sine.easeIn', onComplete: () => this.fade(glow, 200) });
    if (u && !this.quality.still) this.tween({ targets: u.sprite, scaleY: 1.06, duration: Math.max(60, ms / 4), yoyo: true, repeat: 1, ease: 'Sine.easeInOut' });
    const n = this.n(3);
    for (let i = 0; i < n; i++) this.after((ms * i) / n, () => this.floatNote(hand, i, pal, ms * 1.6, cell * (0.9 + Math.random() * 0.5)));
  }

  /** The flight, by form. */
  private fly(c: SpellCast, hand: P, aim: P, pal: Palette, ms: number): void {
    switch (c.look.form) {
      case 'bolt': return this.bolt(c, hand, aim, pal, ms, false);
      case 'darts': return this.darts(c, hand, pal, ms);
      case 'ball': return this.bolt(c, hand, aim, pal, ms, true);
      case 'beam': return this.beamWarm(hand, pal, ms);
      case 'column': return this.columnFall(c, aim, pal, ms);
      case 'storm': return this.stormFall(c, pal, ms);
      case 'volley': return this.volleyUp(c, hand, pal, ms);
      case 'wall': return this.wallRise(c, pal, ms);
      case 'aura': return this.auraStream(c, hand, pal, ms);
      case 'curse': return this.curseClose(c, hand, pal, ms);
      case 'touch': return this.touchReach(c, hand, aim, pal, ms);
      case 'blink': return this.blinkOut(c, hand, pal, ms);
      case 'smite': return this.smiteRaise(aim, pal, ms);
      case 'sow': return this.sowSeed(c, hand, aim, pal, ms);
      case 'gust': return this.gust(c, hand, pal, ms);
      case 'song': return this.songFly(c, hand, pal, ms);
      case 'jeer': return this.jeerFly(c, hand, aim, pal, ms);
      case 'finale': return this.finaleSpin(c, pal, ms);
      case 'nova': case 'bloom': case 'cloud': return;
    }
  }

  /** The landing, by form; `ms` is what is left of the clip. */
  private land(c: SpellCast, aim: P, pal: Palette, ms: number): void {
    const f = c.look.form;
    const big = c.look.shake ?? 0;
    switch (f) {
      case 'bolt': this.pop(aim, c.look.element, pal, big ? 1.1 : 0.8, ms); break;
      case 'darts': break;   // each dart pops as it lands
      case 'ball': this.explosion(c, aim, pal, ms); break;
      case 'beam': this.beam(c, pal, ms); break;
      case 'nova': this.nova(c, pal, ms); break;
      case 'column': for (const p of c.targets.length ? this.bodies(c) : this.areaPoints(c)) this.pop(p, c.look.element, pal, 1, ms); if (c.look.accent) this.explosion(c, aim, PALETTES[c.look.accent], ms, true); break;
      case 'storm': break;   // the stones land as they fall
      case 'volley': break;
      case 'wall': break;
      case 'bloom': this.bloom(c, pal, ms); break;
      case 'aura': for (const p of this.bodies(c)) this.aura(p, c.look.element, pal, ms); break;
      case 'curse': for (const p of this.bodies(c)) this.curse(p, c, pal, ms); break;
      case 'touch': this.pop(aim, c.look.element, pal, 1, ms); break;
      case 'blink': this.blinkIn(c, pal, ms); break;
      case 'cloud': this.cloud(c, pal, ms); break;
      case 'smite': this.smiteFall(c, aim, pal, ms); break;
      case 'sow': this.sowLand(c, pal, ms); break;
      case 'gust': for (const p of c.targets.length ? this.bodies(c) : []) this.pop(p, 'wind', pal, 0.6, ms); break;
      case 'song': this.songLand(c, pal, ms); break;
      case 'jeer': this.jeerLand(c, aim, pal, ms); break;
      case 'finale': this.finaleBurst(c, pal, ms); break;
    }
    if (big) this.shake(big);
    this.linger(c, pal);
  }

  // ---- forms ------------------------------------------------------------------------------------

  /** A missile from the hand to the aim, trailing its element; a ball arcs high and is bigger. */
  private bolt(c: SpellCast, from: P, to: P, pal: Palette, ms: number, ball: boolean): void {
    const cell = this.host.cell;
    const el = c.look.element;
    const g = grain(el, pal);
    const size = ball ? 0.95 : 0.6;
    const halo = this.image(from.x, from.y, TEX.glow, pal.edge, ADD).setScale((cell * size * 1.8) / 64).setAlpha(0.65);
    const orb = this.image(from.x, from.y, TEX.glow, pal.glow, ADD).setScale((cell * size) / 64);
    const core = this.image(from.x, from.y, el === 'frost' ? TEX.shard : el === 'nature' ? TEX.thorn : TEX.star, 0xffffff, ADD).setScale(ball ? 2 : 1.4);
    const trail = this.emitter(0, 0, g.tex, {
      lifespan: g.life * 0.8, speed: { min: 6, max: 22 }, scale: { start: ball ? 2 : 1.3, end: 0 }, alpha: { start: 1, end: 0 },
      ...hue(g), blendMode: g.blend, gravityY: g.gravity * 0.5, frequency: this.quality.density > 0.5 ? 10 : 28, quantity: ball ? 3 : 2,
      rotate: g.rotate ? { min: 0, max: 360 } : 0,
    });
    trail.startFollow(orb);
    const sparks = this.emitter(0, 0, TEX.px, { lifespan: 240, speed: { min: 10, max: 40 }, scale: { start: 1, end: 0 }, color: [0xffffff, pal.glow, pal.edge], blendMode: ADD, frequency: 30 });
    sparks.startFollow(orb);
    let smoke: Phaser.GameObjects.Particles.ParticleEmitter | undefined;
    if (ball && (el === 'fire' || el === 'acid')) {
      smoke = this.emitter(0, 0, TEX.smoke, { lifespan: 700, speed: { min: 2, max: 8 }, scale: { start: 0.4, end: 1.1 }, alpha: { start: 0.35, end: 0 }, tint: [pal.dark, 0x34302c], frequency: 40 });
      smoke.startFollow(orb);
    }
    const arc = ball ? cell * (1.1 + Math.hypot(to.x - from.x, to.y - from.y) / cell / 5) : (el === 'lightning' ? 0 : cell * 0.15);
    const dx = to.x - from.x, dy = to.y - from.y;
    core.setRotation(Math.atan2(dy, dx) + Math.PI / 2);
    const state = { t: 0 };
    this.tween({
      targets: state, t: 1, duration: ms, ease: ball ? 'Sine.easeIn' : 'Quad.easeIn',
      onUpdate: () => {
        const t = state.t;
        const jitter = el === 'lightning' && !this.quality.still ? (Math.random() - 0.5) * cell * 0.25 : 0;
        const x = from.x + dx * t, y = from.y + dy * t - Math.sin(Math.PI * t) * arc + jitter;
        halo.setPosition(x, y); orb.setPosition(x, y); core.setPosition(x, y);
        const throb = 1 + 0.18 * Math.sin(t * 40);
        orb.setScale(((cell * size) / 64) * throb);
        if (ball) core.setAngle(core.angle + 14);
      },
      onComplete: () => { trail.stop(); sparks.stop(); smoke?.stop(); this.reap(trail, g.life); this.reap(sparks, 260); if (smoke) this.reap(smoke, 720); this.kill(halo); this.kill(orb); this.kill(core); },
    });
    // a pale line of light along the way for the bright ones
    if (!ball && (el === 'lightning' || el === 'holy' || el === 'radiant' || el === 'arcane' || el === 'frost')) {
      this.after(ms * 0.5, () => this.streak(from, to, pal, Math.min(300, ms)));
    }
  }

  /** A fading line from `a` to `b` (a bolt's wake). */
  private streak(a: P, b: P, pal: Palette, ms: number): void {
    const gfx = this.graphics();
    gfx.setBlendMode(ADD);
    gfx.lineStyle(3, pal.glow, 0.35).lineBetween(a.x, a.y, b.x, b.y);
    gfx.lineStyle(1, pal.core, 0.7).lineBetween(a.x, a.y, b.x, b.y);
    this.tween({ targets: gfx, alpha: 0, duration: ms, onComplete: () => this.kill(gfx) });
  }

  /** Magic Missile: darts on curving paths, one after another, each popping on its target. */
  private darts(c: SpellCast, from: P, pal: Palette, ms: number): void {
    const n = c.look.count ?? 3;
    const gap = Math.min(130, ms / (n + 1));
    const fly = Math.max(120, ms - gap * (n - 1));
    const targets = c.targets.length ? c.targets : [-1];
    for (let i = 0; i < n; i++) {
      this.after(gap * i, () => {
        const tid = targets[i % targets.length]!;
        const to = (tid >= 0 && this.centreOfUnit(tid)) || this.aimPoint(c);
        const side = (i % 2 === 0 ? 1 : -1) * (0.6 + 0.4 * (i / n));
        const mid = { x: (from.x + to.x) / 2 - (to.y - from.y) * 0.35 * side, y: (from.y + to.y) / 2 + (to.x - from.x) * 0.35 * side - this.host.cell * 0.6 };
        const dart = this.image(from.x, from.y, TEX.glow, pal.edge, ADD).setScale((this.host.cell * 0.9) / 64);
        const tip = this.image(from.x, from.y, TEX.star, 0xffffff, ADD).setScale(1.6);
        const trail = this.emitter(0, 0, TEX.dot, { lifespan: 340, speed: { min: 2, max: 10 }, scale: { start: 1.4, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, pal.glow, pal.edge, pal.dark], blendMode: ADD, frequency: 10, quantity: 2 });
        trail.startFollow(dart);
        const curve = new Phaser.Curves.QuadraticBezier(new Phaser.Math.Vector2(from.x, from.y), new Phaser.Math.Vector2(mid.x, mid.y), new Phaser.Math.Vector2(to.x, to.y));
        const st = { t: 0 };
        const v = new Phaser.Math.Vector2();
        this.tween({
          targets: st, t: 1, duration: fly, ease: 'Sine.easeInOut',
          onUpdate: () => { curve.getPoint(st.t, v); dart.setPosition(v.x, v.y); tip.setPosition(v.x, v.y).setAngle(st.t * 540); },
          onComplete: () => { trail.stop(); this.reap(trail, 360); this.kill(dart); this.kill(tip); this.pop(to, c.look.element, pal, 0.75, 260); },
        });
      });
    }
  }

  /** A beam's moment before it fires: the hand brightens. */
  private beamWarm(hand: P, pal: Palette, ms: number): void {
    const g = this.image(hand.x, hand.y, TEX.glow, pal.core, ADD).setScale((this.host.cell * 0.4) / 64).setAlpha(0.6);
    this.tween({ targets: g, scale: (this.host.cell * 0.9) / 64, alpha: 1, duration: ms, onComplete: () => this.fade(g, 160) });
  }

  /** Lightning (jagged, flickering, forked) or a frost ray (straight, shards bursting on every square). */
  private beam(c: SpellCast, pal: Palette, ms: number): void {
    const from = this.hand(c);
    const pts = c.look.form === 'beam' && c.tiles.length ? c.tiles.map((t) => this.tileP(t)) : [this.aimPoint(c)];
    const end = c.tiles.length ? this.farthest(from, pts) : this.aimPoint(c);
    const el = c.look.element;
    if (el === 'lightning') {
      const gfx = this.graphics(); gfx.setBlendMode(ADD);
      const draw = () => {
        gfx.clear();
        const path = this.jag(from, end, 9, this.host.cell * 0.35);
        gfx.lineStyle(6, pal.edge, 0.35); this.strokePath(gfx, path);
        gfx.lineStyle(3, pal.glow, 0.9); this.strokePath(gfx, path);
        gfx.lineStyle(1, pal.core, 1); this.strokePath(gfx, path);
        // forks
        for (let i = 2; i < path.length - 1; i += 3) {
          const a = path[i]!; const fork = this.jag(a, { x: a.x + (Math.random() - 0.3) * this.host.cell * 1.2, y: a.y + (Math.random() - 0.5) * this.host.cell * 1.4 }, 4, this.host.cell * 0.2);
          gfx.lineStyle(1, pal.glow, 0.8); this.strokePath(gfx, fork);
        }
      };
      draw();
      const flickers = this.quality.still ? 1 : 4;
      for (let i = 1; i < flickers; i++) this.after((ms * 0.55 * i) / flickers, draw);
      this.tween({ targets: gfx, alpha: 0, duration: ms * 0.45, delay: ms * 0.55, onComplete: () => this.kill(gfx) });
      this.flash(pal.glow, 0.22, 140);
      for (const p of pts) this.pop(p, 'lightning', pal, 0.6, ms);
      this.glowAt(from, pal.glow, 1.1, 260);
    } else {
      const gfx = this.graphics(); gfx.setBlendMode(ADD);
      gfx.lineStyle(7, pal.edge, 0.3).lineBetween(from.x, from.y, end.x, end.y);
      gfx.lineStyle(4, pal.glow, 0.8).lineBetween(from.x, from.y, end.x, end.y);
      gfx.lineStyle(1, pal.core, 1).lineBetween(from.x, from.y, end.x, end.y);
      gfx.setAlpha(0);
      this.tween({ targets: gfx, alpha: 1, duration: 70 });
      this.tween({ targets: gfx, alpha: 0, duration: ms * 0.6, delay: ms * 0.35, onComplete: () => this.kill(gfx) });
      // shards burst out of every square it crosses, one after another
      const g = grain(el, pal);
      pts.forEach((p, i) => this.after(i * 40, () => {
        const e = this.emitter(p.x, p.y, TEX.shard, { emitting: false, lifespan: 520, speed: { min: 20, max: 60 }, angle: { min: 200, max: 340 }, gravityY: 120, scale: { start: 1.1, end: 0.6 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: ADD, rotate: { min: 0, max: 360 } });
        this.burst(e, this.n(8), p.x, p.y); this.reap(e, 600);
        this.glowAt(p, pal.glow, 0.6, 300);
      }));
    }
  }

  /** A great burst: the flash, a ball of the element swelling, a shockwave, a ring of it rolling out, a fountain, smoke, a scorch. */
  private explosion(c: SpellCast, at: P, pal: Palette, ms: number, quiet = false): void {
    const cell = this.host.cell;
    const area = this.areaPoints(c);
    const reach = Math.max(cell * 0.9, ...area.map((p) => Math.hypot(p.x - at.x, p.y - at.y) + cell * 0.6));
    const el = c.look.element;
    const g = grain(el, pal);
    if (!quiet) this.flash(pal.glow, 0.16, 160);
    // the body: a ball of the element swelling and burning out (solid, so it reads on bright grass)
    const body = this.image(at.x, at.y, TEX.scorch, pal.edge, Phaser.BlendModes.NORMAL).setScale(0.1).setAlpha(0.95);
    const heart = this.image(at.x, at.y, TEX.glow, pal.glow, ADD).setScale(0.1).setAlpha(1);
    const white = this.image(at.x, at.y, TEX.glow, 0xffffff, ADD).setScale(0.1).setAlpha(1);
    const swell = Math.min(220, ms * 0.4);
    this.tween({ targets: body, scale: (reach * 2.1) / 32, duration: swell, ease: 'Cubic.easeOut' });
    this.tween({ targets: body, alpha: 0, duration: Math.min(520, ms), delay: swell * 0.6, ease: 'Quad.easeIn', onComplete: () => this.kill(body) });
    this.tween({ targets: heart, scale: (reach * 3.2) / 64, duration: swell, ease: 'Cubic.easeOut' });
    this.tween({ targets: heart, alpha: 0, duration: Math.min(620, ms + 120), delay: swell, onComplete: () => this.kill(heart) });
    this.tween({ targets: white, scale: (reach * 1.6) / 64, alpha: 0, duration: swell * 1.4, ease: 'Quad.easeOut', onComplete: () => this.kill(white) });
    this.glowAt(at, pal.edge, (reach * 3) / cell, Math.min(800, ms + 260));
    // the shockwave, flattened onto the ground
    const ring = this.image(at.x, at.y + cell * 0.2, TEX.ring, pal.glow, ADD).setScale(0.1, 0.06).setAlpha(1);
    this.tween({ targets: ring, scaleX: (reach * 2.6) / 48, scaleY: (reach * 1.6) / 48, alpha: 0, duration: Math.min(520, ms + 100), ease: 'Cubic.easeOut', onComplete: () => this.kill(ring) });
    // a ring of the element rolling out along the ground
    const roll = this.emitter(at.x, at.y + cell * 0.2, g.tex, {
      emitting: false, lifespan: { min: g.life * 0.5, max: g.life }, speed: { min: reach * 1.2, max: reach * 1.8 }, angle: { min: 0, max: 360 },
      scaleX: { start: 2, end: 0.4 }, scaleY: { start: 1.4, end: 0.2 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, gravityY: g.gravity * 0.3,
    });
    this.burst(roll, this.n(quiet ? 14 : 30), at.x, at.y + cell * 0.2); this.reap(roll, g.life + 60);
    // the fountain
    const e = this.emitter(at.x, at.y, g.tex, {
      emitting: false, lifespan: { min: g.life * 0.7, max: g.life * 1.5 }, speed: { min: reach * 0.5, max: reach * 2.4 }, angle: { min: 0, max: 360 },
      gravityY: g.gravity, scale: { start: 2.2, end: 0.3 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, rotate: g.rotate ? { min: 0, max: 360 } : 0,
    });
    this.burst(e, this.n(quiet ? 24 : 64), at.x, at.y); this.reap(e, g.life * 1.6);
    // a column of the element climbing out of the heart (the mushroom of a fireball)
    const plume = this.emitter(at.x, at.y, g.tex, { lifespan: { min: g.life * 0.6, max: g.life * 1.1 }, speedY: { min: -reach * 1.6, max: -reach * 0.6 }, speedX: { min: -reach * 0.4, max: reach * 0.4 }, scale: { start: 2.4, end: 0.4 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, frequency: this.quality.density > 0.5 ? 12 : 30, quantity: 2,
      emitZone: { type: 'random', source: new Phaser.Geom.Circle(0, 0, reach * 0.35) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
    this.after(Math.min(360, ms), () => { plume.stop(); this.reap(plume, g.life * 1.2); });
    // the hot core spits bright pixels that arc and fall
    const sp = this.emitter(at.x, at.y, TEX.px, { emitting: false, lifespan: { min: 400, max: 900 }, speed: { min: reach, max: reach * 3.2 }, angle: { min: 190, max: 350 }, gravityY: 220, scale: { start: 1.2, end: 0.5 }, color: [0xffffff, pal.glow, pal.edge], blendMode: ADD });
    this.burst(sp, this.n(26), at.x, at.y); this.reap(sp, 950);
    // every square of the blast takes its own little burst a beat apart
    area.forEach((p, i) => this.after(30 + 35 * i, () => {
      const b = this.emitter(p.x, p.y, g.tex, { emitting: false, lifespan: g.life, speed: { min: 8, max: 34 }, angle: { min: 200, max: 340 }, gravityY: g.gravity, scale: { start: 1.6, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend });
      this.burst(b, this.n(8), p.x, p.y); this.reap(b, g.life + 100);
    }));
    // smoke rolling up, and the ground scorched for a while
    if (el === 'fire' || el === 'radiant' || el === 'acid' || el === 'thunder' || el === 'nature') {
      const smoke = this.emitter(at.x, at.y - cell * 0.4, TEX.smoke, { emitting: false, lifespan: { min: 900, max: 1600 }, speed: { min: 10, max: 28 }, angle: { min: 220, max: 320 }, scale: { start: 0.6, end: 1.8 }, alpha: { start: 0.3, end: 0 }, tint: el === 'nature' ? [0x8a6a3a, 0x5a4a2a] : [pal.dark, 0x3a3634], gravityY: -18 });
      this.after(swell * 2, () => { this.burst(smoke, this.n(10), at.x, at.y - cell * 0.4); });
      this.reap(smoke, 1800);
      const scorch = this.ground(at.x, at.y + cell * 0.3, TEX.scorch, el === 'acid' ? pal.edge : 0x1a100a, el === 'acid' ? ADD : Phaser.BlendModes.NORMAL).setScale((reach * 1.6) / 32, (reach * 0.9) / 32).setAlpha(el === 'acid' ? 0.35 : 0.38);
      this.tween({ targets: scorch, alpha: 0, duration: 2000, delay: 500, onComplete: () => this.kill(scorch) });
    }
  }

  /** A small flash where something lands: a glow, a ring, a spray of its grain. */
  /** docs/72 IT18: a magic weapon's glint on a blow that lands — a small pop in the piece's element
   * on the one it hit (and an accent's colour, if any). */
  glint(unit: number, el: Element, accent?: Element): void {
    const at = this.centreOfUnit(unit);
    if (!at) return;
    this.pop(at, el, PALETTES[el], 0.55, 260);
    if (accent) this.glowAt(at, PALETTES[accent].glow, 0.9, 260);
  }

  private pop(at: P, el: Element, pal: Palette, size: number, ms: number): void {
    const cell = this.host.cell;
    const g = grain(el, pal);
    this.glowAt(at, pal.glow, 1.4 * size, Math.min(360, ms + 60));
    const ring = this.image(at.x, at.y, TEX.ring, pal.core, ADD).setScale(0.05).setAlpha(0.9);
    this.tween({ targets: ring, scale: (cell * 1.1 * size) / 48, alpha: 0, duration: 260, ease: 'Quad.easeOut', onComplete: () => this.kill(ring) });
    const e = this.emitter(at.x, at.y, g.tex, { emitting: false, lifespan: g.life, speed: { min: 20 * size, max: 90 * size }, angle: { min: 0, max: 360 }, gravityY: g.gravity, scale: { start: 1.6, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, rotate: g.rotate ? { min: 0, max: 360 } : 0 });
    this.burst(e, this.n(Math.round(22 * size)), at.x, at.y); this.reap(e, g.life + 60);
    const core = this.image(at.x, at.y, TEX.star, 0xffffff, ADD).setScale(3 * size).setAlpha(1);
    this.tween({ targets: core, scale: 0.5, alpha: 0, angle: 90, duration: 240, onComplete: () => this.kill(core) });
  }

  /** A ring thrown out from the caster: thunder's blast, the guardians' circle, a mending wave. */
  private nova(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const at = this.centreOfUnit(c.caster) ?? this.hand(c);
    const el = c.look.element;
    const rings = el === 'thunder' ? 3 : 2;
    for (let i = 0; i < rings; i++) {
      const ring = this.image(at.x, at.y + cell * 0.2, TEX.ring, i === 0 ? pal.core : pal.glow, ADD).setScale(0.1, 0.07).setAlpha(1);
      this.tween({ targets: ring, scaleX: (cell * (2.6 + i * 0.5)) / 48, scaleY: (cell * (1.9 + i * 0.4)) / 48, alpha: 0, duration: Math.min(520, ms) + i * 90, delay: i * 70, ease: 'Cubic.easeOut', onComplete: () => this.kill(ring) });
    }
    this.glowAt(at, pal.glow, 2.6, Math.min(420, ms));
    const g = grain(el, pal);
    if (el === 'thunder') {
      this.flash(pal.core, 0.16, 100);
      const dust = this.emitter(at.x, at.y + cell * 0.3, TEX.smoke, { emitting: false, lifespan: 700, speed: { min: 50, max: 110 }, angle: { min: 0, max: 360 }, scale: { start: 0.3, end: 0.9 }, alpha: { start: 0.5, end: 0 }, tint: [0xc9c2b0, 0x8f897c] });
      this.burst(dust, this.n(20), at.x, at.y + cell * 0.3); this.reap(dust, 800);
    }
    if (el === 'holy' && (c.look.count ?? 0) > 0) {
      // spirit guardians: bright spirits wheel once round the caster, then flare on what they strike
      const n = c.look.count!;
      for (let i = 0; i < n; i++) {
        const s = this.image(at.x, at.y, TEX.glow, pal.glow, ADD).setScale((cell * 0.4) / 64);
        const st = { a: (i / n) * Math.PI * 2 };
        this.tween({ targets: st, a: st.a + Math.PI * 2, duration: Math.min(560, ms), onUpdate: () => s.setPosition(at.x + Math.cos(st.a) * cell * 0.9, at.y + Math.sin(st.a) * cell * 0.55), onComplete: () => this.fade(s, 160) });
      }
    }
    const e = this.emitter(at.x, at.y, g.tex, { emitting: false, lifespan: g.life, speed: { min: 60, max: 120 }, angle: { min: 0, max: 360 }, gravityY: g.gravity, scale: { start: 1.1, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend });
    this.burst(e, this.n(26), at.x, at.y); this.reap(e, g.life + 80);
    for (const p of c.targets.filter((t) => t !== c.caster).map((t) => this.centreOfUnit(t)).filter((p): p is P => !!p)) this.after(90, () => this.pop(p, el, pal, 0.7, ms));
  }

  /** Something coming down out of the sky onto the aim: radiance, holy fire. */
  private columnFall(c: SpellCast, at: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const spots = c.targets.length && c.look.form === 'column' && !c.tiles.length ? this.bodies(c) : [at];
    for (const p of spots) {
      const h = cell * 7;
      const beam = this.graphics(); beam.setBlendMode(ADD);
      const st = { t: 0 };
      this.tween({
        targets: st, t: 1, duration: ms, ease: 'Quad.easeIn',
        onUpdate: () => {
          beam.clear();
          const bottom = p.y - h + h * st.t, w = cell * (0.35 + 0.4 * st.t);
          beam.fillStyle(pal.edge, 0.18).fillRect(p.x - w, p.y - h, w * 2, bottom - (p.y - h));
          beam.fillStyle(pal.glow, 0.35).fillRect(p.x - w * 0.55, p.y - h, w * 1.1, bottom - (p.y - h));
          beam.fillStyle(pal.core, 0.8).fillRect(p.x - 1.5, p.y - h, 3, bottom - (p.y - h));
        },
        onComplete: () => this.tween({ targets: beam, alpha: 0, duration: 380, onComplete: () => this.kill(beam) }),
      });
      const e = this.emitter(p.x, p.y - h, TEX.px, { lifespan: 300, speedY: { min: 80, max: 160 }, speedX: { min: -8, max: 8 }, scale: { start: 1, end: 0 }, tint: [pal.core, pal.glow], blendMode: ADD, frequency: 30, emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.3, 0, cell * 0.6, h) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
      this.after(ms, () => { e.stop(); this.reap(e, 320); });
    }
  }

  /** Hail (Ice Storm): stones falling slantwise onto the area, each bursting where it lands. */
  private stormFall(c: SpellCast, pal: Palette, ms: number): void {
    const area = this.areaPoints(c);
    const cell = this.host.cell;
    const n = Math.max(4, Math.round((c.look.count ?? 14) * Math.max(0.35, this.quality.density)));
    // a dark cloud gathers over the area first
    const at = this.aimPoint(c);
    const cloud = this.emitter(at.x, at.y - cell * 2.6, TEX.smoke, { lifespan: 1400, speedX: { min: -10, max: 10 }, speedY: { min: -3, max: 3 }, scale: { start: 1.2, end: 2.2 }, alpha: { start: 0.5, end: 0 }, tint: [pal.dark, 0x3a4250], frequency: 60, emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 2, -cell * 0.4, cell * 4, cell * 0.8) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
    this.after(ms + 900, () => { cloud.stop(); this.reap(cloud, 1500); });
    for (let i = 0; i < n; i++) {
      const p = area[Math.floor(Math.random() * area.length)]!;
      const land = { x: p.x + (Math.random() - 0.5) * cell * 0.8, y: p.y + (Math.random() - 0.5) * cell * 0.6 };
      const delay = (i / n) * (ms + 380);
      this.after(delay, () => {
        const s = this.image(land.x - cell * 1.2, land.y - cell * 3.2, TEX.shard, 0xe8f8ff).setScale(2).setRotation(-0.35);
        this.tween({ targets: s, x: land.x, y: land.y, duration: 220, ease: 'Quad.easeIn', onComplete: () => {
          this.kill(s);
          const e = this.emitter(land.x, land.y, TEX.shard, { emitting: false, lifespan: 420, speed: { min: 24, max: 64 }, angle: { min: 200, max: 340 }, gravityY: 200, scale: { start: 1, end: 0.5 }, rotate: { min: 0, max: 360 }, color: [0xffffff, 0xbfe8ff, 0x5aa8e8] });
          this.burst(e, this.n(7), land.x, land.y); this.reap(e, 480);
          this.glowAt(land, pal.glow, 0.5, 200);
        } });
      });
    }
  }

  /** Rain of Arrows: a sheaf loosed upward, then falling on the area. */
  private volleyUp(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const n = Math.max(5, Math.round((c.look.count ?? 12) * Math.max(0.4, this.quality.density)));
    const up = ms * 0.4;
    for (let i = 0; i < Math.min(5, n); i++) {
      const a = this.image(hand.x, hand.y, TEX.arrow, 0xe8dcc0).setRotation((Math.random() - 0.5) * 0.4);
      this.tween({ targets: a, y: hand.y - cell * 6, x: hand.x + (Math.random() - 0.5) * cell, duration: up, delay: i * 40, ease: 'Quad.easeIn', onComplete: () => this.kill(a) });
    }
    const area = this.areaPoints(c);
    for (let i = 0; i < n; i++) {
      const p = area[i % area.length]!;
      const land = { x: p.x + (Math.random() - 0.5) * cell * 0.8, y: p.y + (Math.random() - 0.5) * cell * 0.5 };
      this.after(up + (i / n) * (ms - up + 200), () => {
        const a = this.image(land.x + cell * 0.6, land.y - cell * 4, TEX.arrow, 0xf4ecd8).setRotation(Math.PI + 0.15);
        this.tween({ targets: a, x: land.x, y: land.y, duration: 180, ease: 'Quad.easeIn', onComplete: () => {
          this.tween({ targets: a, alpha: 0, duration: 500, delay: 200, onComplete: () => this.kill(a) });
          const e = this.emitter(land.x, land.y, TEX.px, { emitting: false, lifespan: 260, speed: { min: 10, max: 30 }, angle: { min: 200, max: 340 }, gravityY: 120, tint: [0xc9b48a, 0x8f7a56] });
          this.burst(e, this.n(4), land.x, land.y); this.reap(e, 300);
        } });
      });
    }
    void pal;
  }

  /** Wall of Fire: flames rising square by square along its line. */
  private wallRise(c: SpellCast, pal: Palette, ms: number): void {
    const pts = this.areaPoints(c);
    const g = grain(c.look.element, pal);
    const step = ms / Math.max(1, pts.length);
    pts.forEach((p, i) => this.after(i * step, () => {
      const cell = this.host.cell;
      this.glowAt(p, pal.edge, 1.6, 900);
      const e = this.emitter(p.x, p.y + cell * 0.35, g.tex, {
        lifespan: { min: 360, max: 700 }, speedY: { min: -70, max: -30 }, speedX: { min: -8, max: 8 }, scale: { start: 1.8, end: 0.2 }, alpha: { start: 1, end: 0 },
        ...hue(g), blendMode: g.blend, frequency: this.quality.density > 0.5 ? 18 : 40, quantity: 2,
        emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.42, -2, cell * 0.84, 4) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
      });
      this.after(ms - i * step + 900, () => { e.stop(); this.reap(e, 720); });
      if (i === 0) this.shake(1);
    }));
  }

  /** Vines, roots and thorns breaking out of the ground over the area. */
  private bloom(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const pts = c.look.name === 'Snare' && c.aim ? [this.tileP(c.aim)] : this.areaPoints(c);
    for (const p of pts) {
      const gfx = this.graphics();
      const vines = Array.from({ length: 4 }, (_, i) => {
        const a = (i / 4) * Math.PI * 2 + Math.random() * 0.8;
        return { a, r: cell * (0.3 + Math.random() * 0.25), h: cell * (0.45 + Math.random() * 0.4), curl: (Math.random() - 0.5) * 2 };
      });
      const st = { t: 0 };
      this.tween({
        targets: st, t: 1, duration: Math.min(380, ms * 0.6), ease: 'Back.easeOut',
        onUpdate: () => {
          gfx.clear();
          for (const v of vines) {
            const bx = p.x + Math.cos(v.a) * v.r, by = p.y + cell * 0.3 + Math.sin(v.a) * v.r * 0.5;
            const tx = bx + v.curl * cell * 0.25 * st.t, ty = by - v.h * st.t;
            gfx.lineStyle(3, pal.dark, 1).lineBetween(bx, by, tx, ty);
            gfx.lineStyle(1, pal.edge, 1).lineBetween(bx, by, tx, ty);
            gfx.fillStyle(pal.glow, 1).fillRect(Math.round(tx) - 1, Math.round(ty) - 1, 2, 2);
            gfx.fillStyle(pal.glow, 1).fillRect(Math.round((bx + tx) / 2) + 1, Math.round((by + ty) / 2), 2, 1);
          }
        },
      });
      this.tween({ targets: gfx, alpha: 0, duration: 500, delay: ms + 300, onComplete: () => this.kill(gfx) });
      const e = this.emitter(p.x, p.y + cell * 0.2, TEX.leaf, { emitting: false, lifespan: 800, speed: { min: 14, max: 40 }, angle: { min: 200, max: 340 }, gravityY: 60, rotate: { min: 0, max: 360 }, tint: [pal.glow, pal.edge], scale: { start: 1.2, end: 0.6 }, alpha: { start: 1, end: 0 } });
      this.burst(e, this.n(7), p.x, p.y + cell * 0.2); this.reap(e, 900);
      const dirt = this.emitter(p.x, p.y + cell * 0.3, TEX.px, { emitting: false, lifespan: 420, speed: { min: 18, max: 46 }, angle: { min: 210, max: 330 }, gravityY: 200, tint: [0x6b4a2a, 0x8a6a3a] });
      this.burst(dirt, this.n(8), p.x, p.y + cell * 0.3); this.reap(dirt, 480);
    }
  }

  /** Before an aura settles: motes stream from the caster to a friend. */
  private auraStream(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    for (const to of this.bodies(c)) {
      if (Math.hypot(to.x - hand.x, to.y - hand.y) < this.host.cell * 0.6) continue;
      const e = this.emitter(hand.x, hand.y, TEX.star, { emitting: false, lifespan: ms, scale: { start: 1, end: 0.6 }, tint: [pal.core, pal.glow], blendMode: ADD, moveToX: to.x, moveToY: to.y });
      this.burst(e, this.n(10), hand.x, hand.y); this.reap(e, ms + 40);
    }
  }

  /** A blessing, a ward, a heal settling on a body: a ring at its feet, light rising, a shield's glint. */
  private aura(at: P, el: Element, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const feet = { x: at.x, y: at.y + cell * 0.45 };
    const ring = this.image(feet.x, feet.y, TEX.ring, pal.glow, ADD).setScale((cell * 1.4) / 48, (cell * 0.5) / 48).setAlpha(0);
    this.tween({ targets: ring, alpha: 1, scaleX: (cell * 0.9) / 48, scaleY: (cell * 0.32) / 48, duration: Math.min(300, ms), ease: 'Quad.easeOut', yoyo: true, onComplete: () => this.kill(ring) });
    this.glowAt(at, pal.glow, 1.3, Math.min(520, ms + 120));
    const g = grain(el, pal);
    const up = this.emitter(at.x, feet.y, el === 'steel' ? TEX.px : g.tex, {
      emitting: false, lifespan: { min: 500, max: 900 }, speedY: { min: -46, max: -18 }, speedX: { min: -6, max: 6 }, scale: { start: 1.1, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: ADD,
      emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.4, -2, cell * 0.8, 4) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource },
    });
    this.burst(up, this.n(14), at.x, feet.y); this.reap(up, 950);
    if (el === 'steel' || el === 'arcane' || (el === 'holy' && PALETTES.holy === pal)) {
      const sh = this.image(at.x, at.y - cell * 0.05, el === 'arcane' ? TEX.rune : TEX.shield, pal.core, ADD).setScale(el === 'arcane' ? (cell * 0.95) / 64 : (cell * 0.75) / 24).setAlpha(0);
      this.tween({ targets: sh, alpha: 0.9, duration: 140, yoyo: true, hold: Math.min(260, ms), onComplete: () => this.kill(sh) });
    }
    if (el === 'holy') {
      // a halo over the head
      const halo = this.image(at.x, at.y - cell * 0.62, TEX.ring, pal.glow, ADD).setScale((cell * 0.5) / 48, (cell * 0.18) / 48).setAlpha(0);
      this.tween({ targets: halo, alpha: 1, duration: 160, yoyo: true, hold: Math.min(360, ms), onComplete: () => this.kill(halo) });
    }
  }

  /** Before a curse lands: a dark thread from the caster to the target. */
  private curseClose(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    for (const to of this.bodies(c)) {
      const gfx = this.graphics(); gfx.setBlendMode(c.look.element === 'shadow' ? Phaser.BlendModes.NORMAL : ADD);
      const st = { t: 0 };
      this.tween({
        targets: st, t: 1, duration: ms, ease: 'Sine.easeIn',
        onUpdate: () => {
          gfx.clear();
          const pts = this.wave(hand, { x: hand.x + (to.x - hand.x) * st.t, y: hand.y + (to.y - hand.y) * st.t }, 10, this.host.cell * 0.12, st.t * 10);
          gfx.lineStyle(3, pal.dark, 0.6); this.strokePath(gfx, pts);
          gfx.lineStyle(1, pal.glow, 0.9); this.strokePath(gfx, pts);
        },
        onComplete: () => this.tween({ targets: gfx, alpha: 0, duration: 200, onComplete: () => this.kill(gfx) }),
      });
    }
  }

  /** A sigil closing on a body (a curse, a chill, a binding, a hunter's mark). */
  private curse(at: P, c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const tex = c.look.element === 'wild' ? TEX.reticle : TEX.sigil;
    const s = this.image(at.x, at.y, tex, pal.glow, ADD).setScale((cell * 1.6) / 48).setAlpha(0).setAngle(-60);
    this.tween({ targets: s, scale: (cell * 0.8) / 48, alpha: 1, angle: 0, duration: Math.min(260, ms), ease: 'Back.easeOut' });
    this.tween({ targets: s, alpha: 0, duration: 300, delay: Math.min(260, ms) + 260, onComplete: () => this.kill(s) });
    if (c.look.name === 'Hold Person') {
      // bands of force cinch round the body
      for (let i = 0; i < 3; i++) {
        const band = this.image(at.x, at.y - cell * 0.15 + i * cell * 0.2, TEX.ring, pal.core, ADD).setScale((cell * 1.4) / 48, (cell * 0.4) / 48).setAlpha(0);
        this.tween({ targets: band, scaleX: (cell * 0.62) / 48, scaleY: (cell * 0.2) / 48, alpha: 1, duration: 220, delay: i * 80, ease: 'Back.easeIn' });
        this.tween({ targets: band, alpha: 0, duration: 400, delay: i * 80 + 220 + ms * 0.5, onComplete: () => this.kill(band) });
      }
    }
    const g = grain(c.look.element, pal);
    const e = this.emitter(at.x, at.y, c.look.element === 'frost' ? TEX.flake : TEX.dot, { emitting: false, lifespan: 600, speed: { min: 10, max: 30 }, angle: { min: 0, max: 360 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, gravityY: c.look.element === 'shadow' ? 25 : 0 });
    this.burst(e, this.n(12), at.x, at.y); this.reap(e, 650);
    const u = c.targets.map((t) => this.host.unit(t)).find((x) => x && Math.abs(x.x - at.x) < 2);
    if (u) this.tint(u, pal.glow, Math.min(420, ms + 200));
  }

  /** Inflict Wounds: a hand of shadow reaching out and closing. */
  private touchReach(c: SpellCast, hand: P, to: P, pal: Palette, ms: number): void {
    const gfx = this.graphics();
    const st = { t: 0 };
    const claws = [-0.5, 0, 0.5];
    this.tween({
      targets: st, t: 1, duration: ms, ease: 'Quad.easeIn',
      onUpdate: () => {
        gfx.clear();
        for (const off of claws) {
          const end = { x: hand.x + (to.x - hand.x) * st.t, y: hand.y + (to.y - hand.y) * st.t + off * this.host.cell * 0.3 * (1 - st.t) };
          const pts = this.wave(hand, end, 6, this.host.cell * 0.08, off * 6 + st.t * 8);
          gfx.lineStyle(3, pal.dark, 0.9); this.strokePath(gfx, pts);
          gfx.lineStyle(1, pal.glow, 0.9); this.strokePath(gfx, pts);
        }
      },
      onComplete: () => this.tween({ targets: gfx, alpha: 0, duration: 260, onComplete: () => this.kill(gfx) }),
    });
  }

  /** Blink, going: the caster twists into light. */
  private blinkOut(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const u = this.host.unit(c.caster);
    const at = this.centreOfUnit(c.caster) ?? hand;
    const e = this.emitter(at.x, at.y, TEX.star, { emitting: false, lifespan: ms, tint: [pal.core, pal.glow, pal.edge], blendMode: ADD, moveToX: at.x, moveToY: at.y, scale: { start: 1.2, end: 0.2 },
      emitZone: { type: 'random', source: new Phaser.Geom.Circle(0, 0, this.host.cell * 0.9) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
    this.burst(e, this.n(18), at.x, at.y); this.reap(e, ms + 40);
    if (u) this.tween({ targets: u, scaleX: 0.15, scaleY: 1.5, alpha: 0, duration: ms, ease: 'Back.easeIn' });
  }

  /** Blink, arriving: a starburst where it lands; the figure unfolds out of it. */
  private blinkIn(c: SpellCast, pal: Palette, ms: number): void {
    const at = c.aim ? this.tileP(c.aim) : this.aimPoint(c);
    this.pop(at, 'arcane', pal, 1, ms);
    const u = this.host.unit(c.caster);
    if (u) {
      u.placeAt(c.aim ?? u.tile, this.host.cell);
      u.setScale(0.15, 1.5).setAlpha(0);
      this.tween({ targets: u, scaleX: 1, scaleY: 1, alpha: 1, duration: Math.min(260, ms), ease: 'Back.easeOut' });
    }
  }

  /** Smoke billowing over the area (or the caster, for Hide). */
  private cloud(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const pts = c.tiles.length ? this.areaPoints(c) : [this.centreOfUnit(c.caster) ?? this.hand(c)];
    for (const p of pts) {
      const e = this.emitter(p.x, p.y + cell * 0.2, TEX.smoke, { emitting: false, lifespan: { min: 900, max: 1500 }, speed: { min: 6, max: 26 }, angle: { min: 0, max: 360 }, scale: { start: 0.8, end: 2 }, alpha: { start: 0.75, end: 0 }, tint: [pal.glow, pal.edge, pal.core], gravityY: -8 });
      this.burst(e, this.n(c.tiles.length ? 6 : 10), p.x, p.y + cell * 0.2); this.reap(e, 1600);
    }
    void ms;
  }

  // ---- docs/104: the paladin, the druid and the bard ------------------------------------------

  /** Judgement, rising: a bright blade gathers high over the foe, point down, light running off it. */
  private smiteRaise(at: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const top = { x: at.x - cell * 0.6, y: at.y - cell * 1.35 };
    const glow = this.image(top.x, top.y, TEX.glow, pal.glow, ADD).setScale(0.1).setAlpha(0.8);
    const blade = this.image(top.x, top.y - cell * 0.3, TEX.blade, 0xfff6d8).setScale((cell * 1.5) / 22).setAlpha(0).setAngle(-40);
    const edge = this.image(top.x, top.y - cell * 0.3, TEX.blade, pal.glow, ADD).setScale((cell * 1.62) / 22).setAlpha(0).setAngle(-40);
    this.tween({ targets: [blade, edge], alpha: 1, y: top.y, duration: ms * 0.7, ease: 'Back.easeOut' });
    this.tween({ targets: glow, scale: (cell * 1.5) / 64, duration: ms, ease: 'Sine.easeIn' });
    const motes = this.emitter(top.x, top.y, TEX.px, { lifespan: 260, speed: { min: 6, max: 24 }, angle: { min: 0, max: 360 }, scale: { start: 1, end: 0 }, color: [0xffffff, pal.glow, pal.edge], blendMode: ADD, frequency: this.quality.density > 0.5 ? 20 : 50,
      emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.2, -cell * 0.6, cell * 0.4, cell * 1.2) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
    this.after(ms, () => { motes.stop(); this.reap(motes, 280); this.kill(blade); this.kill(edge); this.fade(glow, 140); });
  }

  /** Judgement, falling: the blade swings down over the foe in a bright arc and the light breaks on it. */
  private smiteFall(c: SpellCast, at: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const r = cell * 1.25;
    const swing = Math.min(170, ms * 0.4);
    const blade = this.image(at.x, at.y, TEX.blade, 0xfff6d8).setScale((cell * 1.5) / 22);
    const edge = this.image(at.x, at.y, TEX.blade, pal.glow, ADD).setScale((cell * 1.62) / 22);
    const arc = this.graphics(); arc.setBlendMode(ADD);
    // the swing runs from up-left over the top and down onto the foe's far side (angles round the foe)
    const a0 = -150, a1 = -20;
    const st = { a: a0 };
    const place = () => {
      const rad = Phaser.Math.DegToRad(st.a);
      // the hilt out on the circle, the point toward the foe: it cuts through as it sweeps
      const x = at.x + Math.cos(rad) * r * 0.75, y = at.y + Math.sin(rad) * r * 0.75;
      for (const b of [blade, edge]) b.setPosition(x, y).setRotation(rad + Math.PI / 2);
      arc.clear();
      // the blade's wake: a crescent of light along the swing (drawn as points, from where it began to where it is)
      const wake = (from: number): P[] => { const out: P[] = []; for (let d = from; d <= st.a + 0.01; d += 8) { const q = Phaser.Math.DegToRad(Math.min(d, st.a)); out.push({ x: at.x + Math.cos(q) * r * 0.85, y: at.y + Math.sin(q) * r * 0.85 }); } return out; };
      const w0 = wake(a0), w1 = wake(Math.min(st.a, a0 + 30));
      if (w0.length > 1) { arc.lineStyle(Math.max(3, cell * 0.22), pal.glow, 0.45); this.strokePath(arc, w0); }
      if (w1.length > 1) { arc.lineStyle(Math.max(1, cell * 0.08), 0xffffff, 1); this.strokePath(arc, w1); }
    };
    place();
    this.tween({ targets: st, a: a1, duration: swing, ease: 'Cubic.easeIn', onUpdate: place, onComplete: () => {
      this.tween({ targets: [blade, edge], alpha: 0, duration: 160, onComplete: () => { this.kill(blade); this.kill(edge); } });
      this.tween({ targets: arc, alpha: 0, duration: Math.min(320, ms), onComplete: () => this.kill(arc) });
      this.flash(pal.glow, 0.16, 140);
      this.pop(at, 'radiant', pal, 1.1, ms);
      if (c.look.accent) this.glowAt(at, PALETTES[c.look.accent].glow, 1.2, 280);
      // a cross of light where it bites
      const cross = this.graphics(); cross.setBlendMode(ADD);
      cross.fillStyle(0xffffff, 1).fillRect(at.x - 1, at.y - cell * 0.7, 2, cell * 1.4).fillRect(at.x - cell * 0.45, at.y - cell * 0.22, cell * 0.9, 2);
      cross.fillStyle(pal.glow, 0.5).fillRect(at.x - 3, at.y - cell * 0.75, 6, cell * 1.5).fillRect(at.x - cell * 0.5, at.y - cell * 0.26, cell * 1, 6);
      this.tween({ targets: cross, alpha: 0, duration: Math.min(420, ms + 100), onComplete: () => this.kill(cross) });
    } });
  }

  /** The druid's shaping, thrown: a seed of the land arcs to the aim, trailing its grain. */
  private sowSeed(c: SpellCast, from: P, to: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const g = grain(c.look.element, pal);
    const orb = this.image(from.x, from.y, TEX.glow, pal.glow, ADD).setScale((cell * 0.5) / 64);
    const seed = this.image(from.x, from.y, c.look.element === 'earth' ? TEX.pebble : TEX.leaf, c.look.element === 'earth' ? pal.edge : pal.glow).setScale(2);
    const trail = this.emitter(0, 0, g.tex, { lifespan: 420, speed: { min: 4, max: 16 }, scale: { start: 1.2, end: 0 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, gravityY: g.gravity * 0.3, frequency: this.quality.density > 0.5 ? 16 : 40, rotate: g.rotate ? { min: 0, max: 360 } : 0 });
    trail.startFollow(orb);
    const arc = cell * (0.8 + Math.hypot(to.x - from.x, to.y - from.y) / cell / 6);
    const st = { t: 0 };
    this.tween({ targets: st, t: 1, duration: ms, ease: 'Sine.easeIn',
      onUpdate: () => { const x = from.x + (to.x - from.x) * st.t, y = from.y + (to.y - from.y) * st.t - Math.sin(Math.PI * st.t) * arc; orb.setPosition(x, y); seed.setPosition(x, y).setAngle(st.t * 540); },
      onComplete: () => { trail.stop(); this.reap(trail, 440); this.kill(orb); this.kill(seed); } });
  }

  /** The druid's shaping, landing: the ground over the area answers (a green pulse, or dust and stones). */
  private sowLand(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const g = grain(c.look.element, pal);
    const pts = this.areaPoints(c);
    const at = this.aimPoint(c);
    const ring = this.ground(at.x, at.y + cell * 0.25, TEX.ring, pal.glow, ADD).setScale(0.1, 0.05).setAlpha(1);
    this.tween({ targets: ring, scaleX: (cell * 3.4) / 48, scaleY: (cell * 1.7) / 48, alpha: 0, duration: Math.min(520, ms + 120), ease: 'Cubic.easeOut', onComplete: () => this.kill(ring) });
    this.glowAt(at, pal.glow, 2.2, Math.min(480, ms + 100));
    pts.slice(0, 12).forEach((p, i) => this.after(i * 25, () => {
      const e = this.emitter(p.x, p.y + cell * 0.25, g.tex, { emitting: false, lifespan: g.life, speed: { min: 14, max: 46 }, angle: { min: 210, max: 330 }, gravityY: g.gravity, scale: { start: 1.3, end: 0.4 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, rotate: g.rotate ? { min: 0, max: 360 } : 0 });
      this.burst(e, this.n(6), p.x, p.y + cell * 0.25); this.reap(e, g.life + 80);
      if (c.look.element === 'earth') {
        const d = this.emitter(p.x, p.y + cell * 0.3, TEX.smoke, { emitting: false, lifespan: 800, speed: { min: 6, max: 22 }, angle: { min: 200, max: 340 }, scale: { start: 0.5, end: 1.2 }, alpha: { start: 0.45, end: 0 }, tint: [0xb89a6a, 0x8a6a3a] });
        this.burst(d, this.n(3), p.x, p.y + cell * 0.3); this.reap(d, 840);
      }
    }));
    for (const b of c.targets.map((t) => this.centreOfUnit(t)).filter((p): p is P => !!p)) this.glowAt(b, pal.edge, 0.9, 300);
  }

  /** Gale: streaks of wind tear along the line from beside the druid, leaves and grit in them. */
  private gust(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const pts = this.areaPoints(c);
    const end = pts.length ? this.farthest(hand, pts) : this.aimPoint(c);
    const ang = Math.atan2(end.y - hand.y, end.x - hand.x);
    const len = Math.hypot(end.x - hand.x, end.y - hand.y) + cell;
    const nx = -Math.sin(ang), ny = Math.cos(ang);
    const n = this.n(10);
    for (let i = 0; i < n; i++) {
      const off = (Math.random() - 0.5) * cell * 0.9;
      const start = { x: hand.x + nx * off, y: hand.y + ny * off };
      this.after((i / n) * ms * 0.7, () => {
        const sk = this.image(start.x, start.y, TEX.streak, i % 2 ? pal.core : pal.glow).setRotation(ang).setScale(1.4 + Math.random(), 1).setAlpha(0.85);
        this.tween({ targets: sk, x: start.x + Math.cos(ang) * len, y: start.y + Math.sin(ang) * len + Math.sin(i) * cell * 0.2, alpha: 0, duration: ms * 0.5, ease: 'Quad.easeIn', onComplete: () => this.kill(sk) });
      });
    }
    const g = grain('wind', pal);
    const leaves = this.emitter(hand.x, hand.y, g.tex, { lifespan: { min: 300, max: 600 }, speed: { min: cell * 3, max: cell * 6 }, angle: { min: Phaser.Math.RadToDeg(ang) - 10, max: Phaser.Math.RadToDeg(ang) + 10 }, rotate: { min: 0, max: 360 }, scale: { start: 1.2, end: 0.6 }, alpha: { start: 1, end: 0 }, ...hue(g), blendMode: g.blend, frequency: this.quality.density > 0.5 ? 26 : 70 });
    this.after(ms, () => { leaves.stop(); this.reap(leaves, 620); });
  }

  /**
   * One note: a ♪ ♫ or ♬ (by `i`) in the palette with a dark shade under it so it reads on grass
   * and snow; returned as a container to move as one.
   */
  private note(x: number, y: number, i: number, pal: Palette, scale: number): Phaser.GameObjects.Container {
    const tex = NOTES[i % NOTES.length]!;
    const shade = this.host.scene.add.image(1, 1, tex).setTint(pal.dark).setAlpha(0.85).setScale(scale);
    const body = this.host.scene.add.image(0, 0, tex).setTint(i % 2 ? pal.glow : pal.edge).setScale(scale);
    const shine = this.host.scene.add.image(0, 0, tex).setTint(pal.core).setBlendMode(ADD).setAlpha(0.35).setScale(scale);
    const box = this.host.scene.add.container(x, y, [shade, body, shine]);
    this.own(box);
    return box;
  }

  /** A note lifting off and floating up `rise` with a sway, fading (a song with no one to go to). */
  private floatNote(from: P, i: number, pal: Palette, ms: number, rise: number): void {
    const cell = this.host.cell;
    const x0 = from.x + (Math.random() - 0.5) * cell * 0.7;
    const n = this.note(x0, from.y, i, pal, 1.2 + Math.random() * 0.4).setAlpha(0);
    const sway = (Math.random() < 0.5 ? -1 : 1) * cell * (0.15 + Math.random() * 0.2);
    const st = { t: 0 };
    this.tween({ targets: st, t: 1, duration: ms, ease: 'Sine.easeOut',
      onUpdate: () => { const t = st.t; n.setPosition(x0 + Math.sin(t * Math.PI * 2) * sway, from.y - rise * t).setAlpha(t < 0.15 ? t / 0.15 : 1 - Math.max(0, (t - 0.6) / 0.4)).setAngle(Math.sin(t * Math.PI * 3) * 14); },
      onComplete: () => this.kill(n) });
  }

  /** Who a song goes to: its targets but the bard itself. */
  private audience(c: SpellCast): P[] {
    return c.targets.filter((t) => t !== c.caster).map((t) => this.centreOfUnit(t)).filter((p): p is P => !!p);
  }

  /** A song played: notes rise off the instrument and drift along curves to the friends or foes it reaches; with no one to go to they float up round the bard. */
  private songFly(c: SpellCast, hand: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const to = this.audience(c);
    const n = Math.max(3, this.n(c.look.count ?? 6));
    const fly = Math.max(200, ms * 0.65);
    const u = this.host.unit(c.caster);
    // the bard plays: a little bob to the beat
    if (u && !this.quality.still) this.tween({ targets: u.sprite, scaleY: 1.06, duration: Math.max(60, ms / 6), yoyo: true, repeat: 2, ease: 'Sine.easeInOut' });
    for (let i = 0; i < n; i++) {
      this.after((i / n) * (ms - fly), () => {
        if (!to.length) { this.floatNote(hand, i, pal, fly + 300, cell * (1.1 + Math.random() * 0.6)); return; }
        const dest = to[i % to.length]!;
        const side = (i % 2 ? 1 : -1) * (0.3 + Math.random() * 0.3);
        const mid = { x: (hand.x + dest.x) / 2 - (dest.y - hand.y) * side * 0.4, y: Math.min(hand.y, dest.y) - cell * (0.8 + Math.random() * 0.5) };
        const curve = new Phaser.Curves.QuadraticBezier(new Phaser.Math.Vector2(hand.x, hand.y), new Phaser.Math.Vector2(mid.x, mid.y), new Phaser.Math.Vector2(dest.x, dest.y - cell * 0.3));
        const nt = this.note(hand.x, hand.y, i, pal, 1.3).setAlpha(0);
        const st = { t: 0 };
        const v = new Phaser.Math.Vector2();
        this.tween({ targets: st, t: 1, duration: fly, ease: 'Sine.easeInOut',
          onUpdate: () => { curve.getPoint(st.t, v); nt.setPosition(v.x + Math.sin(st.t * Math.PI * 4) * cell * 0.08, v.y).setAlpha(Math.min(1, st.t * 6)).setAngle(Math.sin(st.t * Math.PI * 3) * 16); },
          onComplete: () => { this.kill(nt); this.glowAt(dest, pal.glow, 0.6, 200); } });
      });
    }
  }

  /** A song lands: a soft ring and glow on each who hears it, a few notes popping up off them; the bard's own notes keep rising as it resolves. */
  private songLand(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const to = this.audience(c);
    const me = this.centreOfUnit(c.caster) ?? this.hand(c);
    for (const p of to) {
      this.glowAt(p, pal.edge, 1.1, Math.min(420, ms + 80));
      const ring = this.image(p.x, p.y + cell * 0.45, TEX.ring, pal.glow, ADD).setScale((cell * 0.4) / 48, (cell * 0.15) / 48).setAlpha(1);
      this.tween({ targets: ring, scaleX: (cell * 1.2) / 48, scaleY: (cell * 0.42) / 48, alpha: 0, duration: Math.min(420, ms + 60), ease: 'Quad.easeOut', onComplete: () => this.kill(ring) });
      for (let k = 0; k < Math.min(3, this.n(3)); k++) this.after(k * 70, () => this.floatNote({ x: p.x, y: p.y - cell * 0.2 }, k + 1, pal, 700, cell * 0.8));
      if (c.look.accent) this.glowAt(p, PALETTES[c.look.accent].glow, 0.9, 260);
    }
    // the song still sounds round the bard: a ring at its feet and notes lifting off
    const ring = this.image(me.x, me.y + cell * 0.45, TEX.ring, pal.edge, ADD).setScale((cell * 0.5) / 48, (cell * 0.18) / 48).setAlpha(0.9);
    this.tween({ targets: ring, scaleX: (cell * (to.length ? 1.4 : 2.4)) / 48, scaleY: (cell * (to.length ? 0.5 : 0.9)) / 48, alpha: 0, duration: Math.min(520, ms + 120), ease: 'Quad.easeOut', onComplete: () => this.kill(ring) });
    const n = to.length ? this.n(2) : this.n(5);
    for (let k = 0; k < n; k++) this.after(k * 60, () => this.floatNote({ x: me.x, y: me.y - cell * 0.1 }, k, pal, 800 + k * 40, cell * (0.9 + 0.2 * k)));
  }

  /** Vicious Mockery: one sharp purple note, thrown hard like a dart, zigzagging a little. */
  private jeerFly(c: SpellCast, from: P, to: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const nt = this.note(from.x, from.y, 0, pal, 2);
    const trail = this.emitter(0, 0, TEX.dot, { lifespan: 260, speed: { min: 2, max: 10 }, scale: { start: 1.2, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, pal.glow, pal.edge, pal.dark], blendMode: ADD, frequency: 12, quantity: 2 });
    const dx = to.x - from.x, dy = to.y - from.y, len = Math.max(1, Math.hypot(dx, dy));
    const nx = -dy / len, ny = dx / len;
    const st = { t: 0 };
    this.tween({ targets: st, t: 1, duration: ms, ease: 'Quad.easeIn',
      onUpdate: () => {
        const z = (this.quality.still ? 0 : Math.sin(st.t * Math.PI * 5) * cell * 0.1) * (1 - st.t);
        const x = from.x + dx * st.t + nx * z, y = from.y + dy * st.t + ny * z - Math.sin(Math.PI * st.t) * cell * 0.2;
        nt.setPosition(x, y).setAngle(Phaser.Math.RadToDeg(Math.atan2(dy, dx)) * 0.25 + Math.sin(st.t * 20) * 10);
        trail.setPosition(x, y);
      },
      onComplete: () => { trail.stop(); this.reap(trail, 280); this.kill(nt); } });
  }

  /** The jeer lands: a sour purple crack round the foe and the note shatters into little ones. */
  private jeerLand(c: SpellCast, at: P, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    this.pop(at, 'shadow', pal, 0.7, ms);
    const g = this.graphics(); g.setBlendMode(ADD);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      const path = this.jag({ x: at.x + Math.cos(a) * cell * 0.15, y: at.y + Math.sin(a) * cell * 0.15 }, { x: at.x + Math.cos(a) * cell * 0.65, y: at.y + Math.sin(a) * cell * 0.5 }, 3, cell * 0.08);
      g.lineStyle(2, pal.edge, 0.8); this.strokePath(g, path);
      g.lineStyle(1, pal.core, 1); this.strokePath(g, path);
    }
    this.tween({ targets: g, alpha: 0, duration: Math.min(320, ms + 60), onComplete: () => this.kill(g) });
    for (let k = 0; k < this.n(4); k++) {
      const a = Math.random() * Math.PI * 2;
      const n = this.note(at.x, at.y, k + 1, pal, 0.9);
      this.tween({ targets: n, x: at.x + Math.cos(a) * cell * 0.8, y: at.y + Math.sin(a) * cell * 0.5 - cell * 0.3, alpha: 0, angle: (Math.random() - 0.5) * 120, duration: 420, ease: 'Quad.easeOut', onComplete: () => this.kill(n) });
    }
    void c;
  }

  /** Finale, building: notes spiral up round the bard, faster and wider, the light swelling. */
  private finaleSpin(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const at = this.centreOfUnit(c.caster) ?? this.hand(c);
    const n = Math.max(4, this.n(8));
    const glow = this.image(at.x, at.y, TEX.glow, pal.glow, ADD).setScale(0.2).setAlpha(0.4);
    this.tween({ targets: glow, scale: (cell * 2.2) / 64, alpha: 0.9, duration: ms, ease: 'Sine.easeIn', onComplete: () => this.fade(glow, 200) });
    const u = this.host.unit(c.caster);
    if (u && !this.quality.still) this.tween({ targets: u.sprite, scaleY: 1.1, duration: Math.max(60, ms / 8), yoyo: true, repeat: 3, ease: 'Sine.easeInOut' });
    for (let i = 0; i < n; i++) {
      const nt = this.note(at.x, at.y, i, pal, 1.3).setAlpha(0);
      const st = { t: 0 };
      const a0 = (i / n) * Math.PI * 2;
      this.tween({ targets: st, t: 1, duration: ms, ease: 'Sine.easeIn',
        onUpdate: () => {
          const t = st.t, a = a0 + (this.quality.still ? 0 : t * Math.PI * 3), r = cell * (0.35 + 0.6 * t);
          nt.setPosition(at.x + Math.cos(a) * r, at.y + Math.sin(a) * r * 0.5 - cell * 0.6 * t).setAlpha(Math.min(1, t * 4)).setAngle(Math.sin(a) * 18);
        },
        onComplete: () => this.kill(nt) });
    }
  }

  /** Finale, bursting: every note flies out at once in a great ring, the light breaks over the board. */
  private finaleBurst(c: SpellCast, pal: Palette, ms: number): void {
    const cell = this.host.cell;
    const at = this.centreOfUnit(c.caster) ?? this.hand(c);
    this.flash(pal.glow, 0.18, 160);
    // the edge colour, not the near-white glow: added on bright grass a big pale glow reads as a white blob
    this.glowAt(at, pal.edge, 2.6, Math.min(700, ms + 200));
    for (let i = 0; i < 2; i++) {
      const ring = this.image(at.x, at.y + cell * 0.2, TEX.ring, i ? pal.glow : pal.core, ADD).setScale(0.1, 0.07).setAlpha(1);
      this.tween({ targets: ring, scaleX: (cell * (4 + i)) / 48, scaleY: (cell * (2.6 + i * 0.6)) / 48, alpha: 0, duration: Math.min(600, ms) + i * 120, delay: i * 80, ease: 'Cubic.easeOut', onComplete: () => this.kill(ring) });
    }
    const n = Math.max(8, this.n(c.look.count ?? 24));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.2;
      const r = cell * (1.8 + Math.random() * 1.2);
      const nt = this.note(at.x, at.y, i, pal, 1.2 + Math.random() * 0.6);
      this.tween({ targets: nt, x: at.x + Math.cos(a) * r, y: at.y + Math.sin(a) * r * 0.6 - cell * 0.4, angle: (Math.random() - 0.5) * 90, duration: Math.min(700, ms + 160), ease: 'Cubic.easeOut' });
      this.tween({ targets: nt, alpha: 0, duration: 360, delay: Math.min(700, ms + 160) * 0.55, onComplete: () => this.kill(nt) });
    }
    const sp = this.emitter(at.x, at.y, TEX.star, { emitting: false, lifespan: { min: 300, max: 700 }, speed: { min: cell * 1.5, max: cell * 4 }, angle: { min: 0, max: 360 }, scale: { start: 1.2, end: 0 }, color: [0xffffff, pal.glow, pal.edge], blendMode: ADD });
    this.burst(sp, this.n(24), at.x, at.y); this.reap(sp, 720);
    for (const p of this.audience(c)) this.after(110, () => this.pop(p, 'music', pal, 0.6, ms));
  }

  /** What is left for a moment after it lands. */
  private linger(c: SpellCast, pal: Palette): void {
    const kind = c.look.linger;
    if (!kind || this.quality.density < 0.3) return;
    const cell = this.host.cell;
    const f = c.look.form;
    const pts = f === 'bolt' || f === 'darts' || f === 'aura' || f === 'touch' || f === 'smite' || f === 'song' || f === 'jeer' ? this.bodies(c) : this.areaPoints(c);
    for (const p of pts.slice(0, 9)) {
      switch (kind) {
        case 'embers': {
          const e = this.emitter(p.x, p.y + cell * 0.3, TEX.px, { lifespan: { min: 600, max: 1200 }, speedY: { min: -30, max: -10 }, speedX: { min: -8, max: 8 }, tint: [0xffd27a, 0xff8a3a, 0xff5a1f], blendMode: ADD, frequency: 90, alpha: { start: 1, end: 0 },
            emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.4, -cell * 0.1, cell * 0.8, cell * 0.2) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
          this.after(1300, () => { e.stop(); this.reap(e, 1300); });
          break;
        }
        case 'frost': {
          const f = this.ground(p.x, p.y + cell * 0.3, TEX.scorch, 0xe4f6ff).setScale((cell * 1.1) / 32, (cell * 0.5) / 32).setAlpha(0.55);
          this.tween({ targets: f, alpha: 0, duration: 1600, delay: 500, onComplete: () => this.kill(f) });
          const e = this.emitter(p.x, p.y + cell * 0.25, TEX.star, { lifespan: 400, scale: { start: 0.8, end: 0 }, tint: [0xffffff, pal.glow], blendMode: ADD, frequency: 260,
            emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.4, -cell * 0.1, cell * 0.8, cell * 0.2) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
          this.after(1500, () => { e.stop(); this.reap(e, 450); });
          break;
        }
        case 'sparks': {
          const e = this.emitter(p.x, p.y, TEX.px, { lifespan: 140, speed: { min: 30, max: 70 }, angle: { min: 0, max: 360 }, tint: [0xffffff, pal.glow], blendMode: ADD, frequency: 140, quantity: 2 });
          this.after(700, () => { e.stop(); this.reap(e, 200); });
          break;
        }
        case 'motes': {
          const e = this.emitter(p.x, p.y + cell * 0.3, TEX.px, { lifespan: { min: 700, max: 1100 }, speedY: { min: -22, max: -8 }, speedX: { min: -5, max: 5 }, tint: [pal.core, pal.glow], blendMode: ADD, frequency: 120, alpha: { start: 1, end: 0 },
            emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.4, -cell * 0.3, cell * 0.8, cell * 0.4) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
          this.after(1000, () => { e.stop(); this.reap(e, 1100); });
          break;
        }
        case 'smoke': {
          const e = this.emitter(p.x, p.y + cell * 0.2, TEX.smoke, { lifespan: 1400, speed: { min: 2, max: 8 }, angle: { min: 0, max: 360 }, scale: { start: 1, end: 1.8 }, alpha: { start: 0.35, end: 0 }, tint: [pal.glow, pal.edge], frequency: 260 });
          this.after(1200, () => { e.stop(); this.reap(e, 1500); });
          break;
        }
        case 'puddle': {
          const f = this.ground(p.x, p.y + cell * 0.32, TEX.scorch, pal.glow).setScale((cell * 0.9) / 32, (cell * 0.36) / 32).setAlpha(0.6);
          this.tween({ targets: f, alpha: 0, duration: 1600, delay: 500, onComplete: () => this.kill(f) });
          const e = this.emitter(p.x, p.y + cell * 0.3, TEX.dot, { lifespan: 500, speedY: { min: -14, max: -6 }, scale: { start: 0.6, end: 1 }, alpha: { start: 0.9, end: 0 }, tint: [pal.glow, pal.core], blendMode: ADD, frequency: 200,
            emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(-cell * 0.35, -2, cell * 0.7, 4) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource } });
          this.after(1500, () => { e.stop(); this.reap(e, 550); });
          break;
        }
        case 'leaves': {
          const e = this.emitter(p.x, p.y - cell * 0.8, TEX.leaf, { lifespan: 1300, speedY: { min: 10, max: 24 }, speedX: { min: -14, max: 14 }, rotate: { min: 0, max: 360 }, tint: [pal.glow, pal.edge, 0xc9a24a], frequency: 220, alpha: { start: 1, end: 0 } });
          this.after(1200, () => { e.stop(); this.reap(e, 1400); });
          break;
        }
      }
    }
  }

  // ---- little helpers ---------------------------------------------------------------------------

  /** Particle count thinned by the intensity setting. */
  private n(base: number): number { return Math.max(1, Math.round(base * Math.max(0.15, this.quality.density))); }

  private image(x: number, y: number, key: string, tint: number, blend: Phaser.BlendModes = Phaser.BlendModes.NORMAL): Phaser.GameObjects.Image {
    const img = this.host.scene.add.image(x, y, key).setTint(tint).setBlendMode(blend);
    this.own(img);
    return img;
  }

  /** An image lying on the ground under the figures. */
  private ground(x: number, y: number, key: string, tint: number, blend: Phaser.BlendModes = Phaser.BlendModes.NORMAL): Phaser.GameObjects.Image {
    const img = this.host.scene.add.image(x, y, key).setTint(tint).setBlendMode(blend);
    if (this.host.addGround) this.host.addGround(img); else this.host.add(img);
    this.live.add(img);
    this.expire(img);
    return img;
  }

  private graphics(): Phaser.GameObjects.Graphics { const g = this.host.scene.add.graphics(); this.own(g); return g; }

  /** Explodes `n` grains at board point (x, y): Phaser 4 places an explosion relative to its emitter. */
  private burst(e: Phaser.GameObjects.Particles.ParticleEmitter, n: number, x: number, y: number): void { e.explode(n, x - e.x, y - e.y); }

  private emitter(x: number, y: number, tex: string, cfg: EmitCfg): Phaser.GameObjects.Particles.ParticleEmitter {
    const e = this.host.scene.add.particles(x, y, tex, cfg);
    this.own(e);
    return e;
  }

  private own(o: Phaser.GameObjects.GameObject): void { this.host.add(o); this.live.add(o); this.expire(o); }

  /**
   * Every piece of a spell is gone within `MAX_LIFE_MS` whatever happens to its tweens: the scene
   * kills all tweens when a new board arrives, which would strand a fading sigil on the board.
   */
  private expire(o: Phaser.GameObjects.GameObject): void { this.after(MAX_LIFE_MS, () => { if (this.live.has(o)) this.kill(o); }); }

  private kill(o: Phaser.GameObjects.GameObject): void { this.live.delete(o); o.destroy(); }

  /** Destroys an emitter once its last particle has had `ms` to die. */
  private reap(e: Phaser.GameObjects.Particles.ParticleEmitter, ms: number): void { this.after(ms + 60, () => { if (this.live.has(e)) this.kill(e); }); }

  private fade(o: Phaser.GameObjects.Image, ms: number): void { this.tween({ targets: o, alpha: 0, duration: ms, onComplete: () => this.kill(o) }); }

  private glowAt(p: P, tint: number, cells: number, ms: number): void {
    const g = this.image(p.x, p.y, TEX.glow, tint, ADD).setScale((this.host.cell * cells * 0.3) / 64).setAlpha(1);
    this.tween({ targets: g, scale: (this.host.cell * cells) / 64, duration: ms * 0.35, ease: 'Quad.easeOut' });
    this.tween({ targets: g, alpha: 0, duration: ms * 0.75, delay: ms * 0.25, ease: 'Quad.easeIn', onComplete: () => this.kill(g) });
  }

  private tween(cfg: Phaser.Types.Tweens.TweenBuilderConfig): void { this.host.scene.tweens.add(cfg); }

  private after(ms: number, fn: () => void): void {
    const t = this.host.scene.time.delayedCall(Math.max(0, ms), () => { this.timers = this.timers.filter((x) => x !== t); fn(); });
    this.timers.push(t);
  }

  private shake(level: number): void {
    if (this.quality.still || !this.host.camera || level <= 0) return;
    this.host.camera.shake(level >= 2 ? 260 : 150, level >= 2 ? 0.006 : 0.003);
  }

  /** The whole board blinks the element's colour (off with the lightning-flash setting). */
  private flash(tint: number, alpha: number, ms: number): void {
    const size = this.host.boardSize?.();
    if (!this.quality.flashes || !size) return;
    const r = this.host.scene.add.rectangle(size.w / 2, size.h / 2, size.w, size.h, tint, alpha).setBlendMode(ADD);
    this.own(r);
    this.tween({ targets: r, alpha: 0, duration: ms, ease: 'Quad.easeOut', onComplete: () => this.kill(r) });
  }

  /** A body takes on the spell's colour for a moment. */
  private tint(u: UnitSprite, colour: number, ms: number): void {
    u.sprite.setTint(colour).setTintMode(Phaser.TintModes.MULTIPLY);
    this.after(ms, () => u.sprite.clearTint());
  }

  private farthest(from: P, pts: P[]): P {
    return pts.reduce((a, p) => (Math.hypot(p.x - from.x, p.y - from.y) > Math.hypot(a.x - from.x, a.y - from.y) ? p : a), pts[0]!);
  }

  /** A jagged path (lightning). */
  private jag(a: P, b: P, n: number, amp: number): P[] {
    const out: P[] = [a];
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.max(1, Math.hypot(dx, dy));
    const nx = -dy / len, ny = dx / len;
    for (let i = 1; i < n; i++) { const t = i / n, o = (Math.random() - 0.5) * 2 * amp; out.push({ x: a.x + dx * t + nx * o, y: a.y + dy * t + ny * o }); }
    out.push(b);
    return out;
  }

  /** A sine-wavy path (a curse's thread, a shadow's claw). */
  private wave(a: P, b: P, n: number, amp: number, phase: number): P[] {
    const out: P[] = [];
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.max(1, Math.hypot(dx, dy));
    const nx = -dy / len, ny = dx / len;
    for (let i = 0; i <= n; i++) { const t = i / n, o = Math.sin(t * Math.PI * 3 + phase) * amp * Math.sin(t * Math.PI); out.push({ x: a.x + dx * t + nx * o, y: a.y + dy * t + ny * o }); }
    return out;
  }

  private strokePath(g: Phaser.GameObjects.Graphics, pts: P[]): void {
    g.beginPath(); g.moveTo(pts[0]!.x, pts[0]!.y);
    for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
    g.strokePath();
  }
}
